import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { restaurantApi } from "@/api/restaurant";
import { useAuthStore } from "@/stores/authStore";

const registerSchema = z
  .object({
    restaurantName: z
      .string()
      .min(2, "O nome do restaurante deve ter pelo menos 2 caracteres"),
    userName: z.string().min(2, "O nome deve ter pelo menos 2 caracteres"),
    userEmail: z.string().email("Por favor, insira um email válido"),
    userPassword: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.userPassword === data.confirmPassword, {
    message: "As senhas não coincidem",
    path: ["confirmPassword"],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

function isDuplicateEmailError(err: unknown) {
  const axiosErr = err as {
    response?: { status?: number; data?: { detail?: string; title?: string } };
  };
  const status = axiosErr.response?.status;
  const data = axiosErr.response?.data;

  return (
    status === 409 ||
    (status === 400 &&
      [data?.detail, data?.title].some((message) =>
        message?.toLowerCase().includes("user already exists")
      ))
  );
}

export function RegisterPage() {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      await restaurantApi.register({
        restaurantName: data.restaurantName,
        userName: data.userName,
        userEmail: data.userEmail,
        userPassword: data.userPassword,
      });

      await checkAuth();
      navigate("/verify-email", { replace: true });
    } catch (err: unknown) {
      if (isDuplicateEmailError(err)) {
        setError("Já existe uma conta com este email");
      } else {
        setError("Ocorreu um erro. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-2 w-fit"
            onClick={() => navigate("/login")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao login
          </Button>
          <CardTitle className="text-2xl font-bold">
            Crie sua conta
          </CardTitle>
          <CardDescription>
            Configure seu restaurante no Swift Menu
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="restaurantName">Nome do Restaurante</Label>
              <Input
                id="restaurantName"
                placeholder="Meu Restaurante"
                {...register("restaurantName")}
                aria-invalid={!!errors.restaurantName}
              />
              {errors.restaurantName && (
                <p className="text-sm text-destructive">
                  {errors.restaurantName.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="userName">Seu Nome</Label>
              <Input
                id="userName"
                placeholder="João Silva"
                {...register("userName")}
                aria-invalid={!!errors.userName}
              />
              {errors.userName && (
                <p className="text-sm text-destructive">
                  {errors.userName.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="userEmail">Email</Label>
              <Input
                id="userEmail"
                type="email"
                placeholder="voce@exemplo.com"
                {...register("userEmail")}
                aria-invalid={!!errors.userEmail}
              />
              {errors.userEmail && (
                <p className="text-sm text-destructive">
                  {errors.userEmail.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="userPassword">Senha</Label>
              <Input
                id="userPassword"
                type="password"
                placeholder="••••••••"
                {...register("userPassword")}
                aria-invalid={!!errors.userPassword}
              />
              {errors.userPassword && (
                <p className="text-sm text-destructive">
                  {errors.userPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                {...register("confirmPassword")}
                aria-invalid={!!errors.confirmPassword}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Criar Conta
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
